<template>
  <section class="financial__panel financial__overview" aria-labelledby="financial-overview-title">
    <h2 id="financial-overview-title" tabindex="-1">Customers</h2>
    <p v-if="unmatched" class="financial__help">
      {{ numberLabel(unmatched) }} carrier {{ unmatched === 1 ? 'cost has' : 'costs have' }} no known shipment ·
      <button class="financial__link" type="button" @click="goShipments">Show in Shipments</button>
    </p>
    <!-- Customers with nothing left to bill, billed or excluded, use the same table, folded under the ones with work. -->
    <component
      :is="table.folded ? 'details' : 'div'"
      v-for="table in tables"
      :key="table.key"
      :class="{ 'financial__overview-done': table.folded }"
    >
      <summary v-if="table.folded">
        {{ numberLabel(table.rows.length) }} {{ table.rows.length === 1 ? 'customer' : 'customers' }} with nothing left to bill
      </summary>
      <div class="financial__table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Customer</th>
              <th scope="col" class="financial__numeric">Rows ready</th>
              <th scope="col" class="financial__numeric">Rows to check</th>
              <th scope="col" class="financial__numeric">Approved, to bill</th>
              <th scope="col" class="financial__numeric">Billed</th>
              <th scope="col" class="financial__numeric">Quoted price (SEK)</th>
              <th scope="col" class="financial__numeric">Actual carrier cost</th>
              <th scope="col" class="financial__numeric">Customer price (approved + ready)</th>
              <th scope="col" class="financial__numeric">Margin</th>
              <th scope="col"><span class="visually-hidden">Action</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in table.rows" :key="row.id">
              <td class="financial__overview-source">
                <strong>{{ row.label }}</strong>
                <small>{{ sourceOf(row) }}</small>
              </td>
              <td class="financial__numeric">
                <strong>{{ numberLabel(row.ready) }}</strong> · {{ amount(row.readyAmount, row) }}
              </td>
              <td class="financial__numeric">
                <strong :class="{ financial__attention: row.check }">{{ numberLabel(row.check) }}</strong>
              </td>
              <td class="financial__numeric">{{ numberLabel(row.toBill) }}<small v-if="row.quoteToBill">{{ row.quoteToBill }} quoted bookings</small></td>
              <td class="financial__numeric">
                {{ numberLabel(row.billed) }}<small v-if="row.excluded">{{ numberLabel(row.excluded) }} excluded</small>
              </td>
              <td class="financial__numeric">{{ amountIn(row.quotedPrice, 'SEK') }}</td>
              <td class="financial__numeric">{{ amount(row.cost, row) }}</td>
              <td class="financial__numeric">{{ amount(row.price, row) }}</td>
              <td class="financial__numeric" :class="{ financial__negative: isNegative(row.margin) }">
                {{ marginOf(row) }}<small v-if="percentOf(row)">{{ percentOf(row) }} on cost</small>
              </td>
              <td>
                <button
                  class="financial__button financial__button--small"
                  type="button"
                  :aria-label="`Review ${row.label} before approving`"
                  @click="review(row)"
                >
                  Review
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="!table.folded">
            <tr>
              <th scope="row">All customers</th>
              <td class="financial__numeric">
                {{ numberLabel(totals.ready) }} ·
                {{ totals.readyAmount === null ? 'several currencies' : amountIn(totals.readyAmount, totals.currency) }}
              </td>
              <td class="financial__numeric">{{ numberLabel(totals.check) }}</td>
              <td class="financial__numeric">{{ numberLabel(totals.toBill) }}</td>
              <td class="financial__numeric">{{ numberLabel(totals.billed) }}</td>
              <td colspan="5"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </component>
    <div v-if="!customers.length && !unmatched" class="financial__empty">
      <mdicon name="file-table-outline" :size="32" aria-hidden="true" />
      <strong>No customers in this selection</strong>
      <p>{{ emptyText }}</p>
      <button class="financial__button" type="button" @click="goInvoices">Import a carrier invoice</button>
      <button class="financial__link" type="button" @click="goShipments">Show in Shipments</button>
    </div>
  </section>
</template>
<script lang="ts">
import type { FinancialApprovalOverviewTables, FinancialApprovalOverviewTotals } from '@/types/Components/FinancialViewState'
import type { FinancialOverviewRow } from '@/types/Models/Financial'
import type { ComputedRef } from 'vue'
import { computed, defineComponent, type PropType } from 'vue'
import {
  financialAmountLabel,
  financialDifferenceLabel,
  financialPercent,
  sumFinancialAmounts,
} from '@/services/FinancialAmounts'

export default defineComponent({
  name: 'FinancialApprovalOverview',
  props: {
    emptyText: { type: String, default: 'Load shipments or match a carrier invoice to see its customers.' },
    customers: { type: Array as PropType<FinancialOverviewRow[]>, required: true },
    // Carrier costs that belong to no known shipment, and so to no customer yet.
    unmatched: { type: Number, default: 0 },
  },
  emits: ['review', 'go-invoices', 'go-shipments'],
  setup(props, { emit }) {
    // A customer with nothing left to approve, exclude or bill is folded, so the list only grows with open work. Its rows
    // are billed, excluded, or both.
    const nothingToBill = (row: FinancialOverviewRow): boolean => row.ready + row.check + row.toExclude + row.toBill + (row.quoteToBill ?? 0) === 0
    const tables: ComputedRef<FinancialApprovalOverviewTables> = computed(() =>
      [
        { key: 'open', folded: false, rows: props.customers.filter((row) => !nothingToBill(row)) },
        { key: 'done', folded: true, rows: props.customers.filter(nothingToBill) },
      ].filter((table) => table.rows.length),
    )
    // Totals over all customers: every cost row belongs to one customer. Amounts only add up within one currency.
    const totals: ComputedRef<FinancialApprovalOverviewTotals> = computed(() => {
      const rows = props.customers
      const currencies = [...new Set(rows.map((row) => row.currency))]
      return {
        ready: rows.reduce((total, row) => total + row.ready, 0),
        readyAmount:
          currencies.length <= 1 && rows.every((row) => row.readyAmount !== null)
            ? sumFinancialAmounts(rows.map((row) => row.readyAmount!))
            : null,
        currency: currencies.length === 1 ? currencies[0] : '',
        check: rows.reduce((total, row) => total + row.check, 0),
        toBill: rows.reduce((total, row) => total + row.toBill, 0),
        billed: rows.reduce((total, row) => total + row.billed, 0),
      }
    })
    // One source line: the customer's shipments with a carrier cost, and where and when the costs were imported.
    const sourceOf = (row: FinancialOverviewRow): string => row.meta.join(' · ')
    // Each row is labelled with its own invoices' currency; mixed currencies are shown without one.
    const amount = (value: string | null, row: FinancialOverviewRow): string => financialAmountLabel(value, row.currency)
    const amountIn = (value: string, currency: string): string => financialAmountLabel(value, currency)
    const marginOf = (row: FinancialOverviewRow): string =>
      row.margin === null ? '—' : financialDifferenceLabel(row.margin, row.currency)
    const numberLabel = (value: number): string => value.toLocaleString('en-GB')
    const isNegative = (value: string | null): boolean => value !== null && Number(value) < 0
    const percentOf = (row: FinancialOverviewRow): string | null =>
      row.margin === null || row.pricedCost === null ? null : financialPercent(row.margin, row.pricedCost)
    const review = (row: FinancialOverviewRow): void => {
      emit('review', row.id)
    }
    const goInvoices = (): void => {
      emit('go-invoices')
    }
    // The shipments of the period, and the costs without a known shipment, are listed under Shipments.
    const goShipments = (): void => {
      emit('go-shipments')
    }
    return {
      tables,
      totals,
      sourceOf,
      amount,
      amountIn,
      marginOf,
      numberLabel,
      isNegative,
      percentOf,
      review,
      goInvoices,
      goShipments,
    }
  },
})
</script>
